import { ROUTE } from '@/config';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AnalyticsIcon,
  CouponIcon,
  DashboardIcon,
  FileIcon,
  FinanceIcon,
  OrdersIcon,
  PickupsIcon,
} from '../Icons';
import boxyLogo from '@/assets/boxy_logo.svg';
import boxyIcon from '@/assets/boxy_icon.svg';
import { useAuthStore } from '@/stores/authStore';
import { useBulkImportStore } from '@/stores';
import { getSidebarRouteInfo } from '@/utils';
import { useUserStore } from '@/stores';
import { useCustomerIO } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { BookUser, ChevronRight, LogOut, Package, Plus, ShieldAlert } from 'lucide-react';
import { RtlIcon } from '@/components/ui/rtl-icon';
import { cn } from '@/lib/utils';
import profileIcon from '@/assets/settings/profile.svg';
import locationsIcon from '@/assets/settings/locations.svg';
import teamIcon from '@/assets/settings/team.svg';
import accessTokensIcon from '@/assets/settings/access-tokens.svg';
import webhooksIcon from '@/assets/settings/webhooks.svg';
import shippingRulesIcon from '@/assets/settings/shipping-rules.svg';

type SidebarItem = {
  key: string;
  label: string;
  disabled: boolean;
  renderIcon: (color: string) => JSX.Element;
};

const Sidebar = () => {
  const clearToken = useAuthStore((state) => state.clearToken);
  const { user, setUser } = useUserStore((state) => state);
  const { clearPendingImport } = useBulkImportStore();

  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const { identifyUser } = useCustomerIO();
  const hasValidatedRef = useRef<string | null>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [railHover, setRailHover] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ bottom: 0, left: 0, right: 0, width: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const portalMenuRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);

  const handleLogOut = useCallback(() => {
    clearToken?.();
    setUser(undefined);
    clearPendingImport();
    navigate(ROUTE.LOGIN);
    queryClient.removeQueries(['user']);
  }, [clearToken, clearPendingImport, navigate, queryClient, setUser]);

  useEffect(() => {
    if (!user || !user.uid) return;

    if (hasValidatedRef.current === user.uid) return;
    hasValidatedRef.current = user.uid;

    identifyUser(user.uid, {
      phone: user.phone,
      user_type: 'MERCHANT',
      email: user.email,
      firstname: user.first_name,
      lastname: user.last_name,
      fullname: `${user.first_name}${user.last_name}`,
    });

    if (user.type !== 'MERCHANT') {
      message?.error(t('wrong_user_type'));
      handleLogOut();
    }
    if (!user.verified_at) {
      message?.error(t('user_not_verified'));
    }
  }, [user, identifyUser, handleLogOut, t]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        portalMenuRef.current &&
        !portalMenuRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedTab = getSidebarRouteInfo(location.pathname);
  const activeTopNavKey =
    selectedTab?.name === 'Unknown' ? ROUTE.DASHBOARD : `/${selectedTab?.location}`;
  const isTicketCenterActive = location.pathname.startsWith(ROUTE.TICKET_CENTER);

  const topNavItems = useMemo<SidebarItem[]>(
    () => [
      {
        key: ROUTE.DASHBOARD,
        label: t('overview'),
        disabled: !hasPermission(PermissionName.DASHBOARD),
        renderIcon: (color) => <DashboardIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.ANALYTICS,
        label: t('analytics'),
        disabled: !hasPermission(PermissionName.DASHBOARD),
        renderIcon: (color) => <AnalyticsIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.ORDERS,
        label: t('orders'),
        disabled: !hasPermission(PermissionName.ORDER_AND_SHIPMENT),
        renderIcon: (color) => <OrdersIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.PICKUPS,
        label: t('shipments'),
        disabled: !hasPermission(PermissionName.ORDER_AND_SHIPMENT),
        renderIcon: (color) => <PickupsIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.FINANCE,
        label: t('finance'),
        disabled: !hasPermission(PermissionName.FINANCE),
        renderIcon: (color) => <FinanceIcon width={18} height={18} color={color} />,
      },
      {
        key: ROUTE.CLAIMS,
        label: t('claims'),
        disabled: !hasPermission(PermissionName.VIEW_CLAIMS),
        renderIcon: (color) => <ShieldAlert width={18} height={18} color={color} />,
      },
    ],
    [t, hasPermission],
  );

  const accountItems = useMemo(
    () => [
      {
        title: t('profile'),
        node: <img src={profileIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_PROFILE,
      },
      {
        title: t('location'),
        node: <img src={locationsIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_LOCATIONS,
      },
      {
        title: t('team_members'),
        node: <img src={teamIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_TEAM_MEMBERS,
      },
      {
        title: t('carrier'),
        node: <img src={shippingRulesIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_CARRIERS,
      },
      {
        title: t('shipping_rules.title'),
        node: <img src={shippingRulesIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_SHIPPING_RULES,
      },
      {
        title: t('file'),
        node: <FileIcon width={20} height={20} />,
        path: ROUTE.SETTINGS_FILES,
      },
      {
        title: t('address_book'),
        node: <BookUser size={20} className='shrink-0' />,
        path: ROUTE.SETTINGS_ADDRESS_BOOK,
      },
      {
        title: t('product_catalog'),
        node: <Package size={20} className='shrink-0' />,
        path: ROUTE.SETTINGS_PRODUCT_CATALOG,
      },
    ],
    [t],
  );

  const integrationItems = useMemo(
    () => [
      {
        title: t('access_tokens'),
        node: <img src={accessTokensIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_ACCESS_TOKENS,
      },
      {
        title: t('web_hooks'),
        node: <img src={webhooksIcon} alt='' className='h-5 w-5 shrink-0' />,
        path: ROUTE.SETTINGS_WEBHOOKS,
      },
    ],
    [t],
  );

  const canCreateOrder = hasPermission(PermissionName.CREATE_ORDER);
  const canOpenTicketCenter = hasPermission(PermissionName.DASHBOARD);

  return (
    <div
      className='sd-scope sd-font hidden md:flex relative m-4 ltr:mr-0 rtl:ml-0 sticky top-4 self-start z-20'
    >
      <aside
        className={cn(
          `flex h-[calc(100vh-32px)] flex-col border bg-sd-card rounded-2xl shadow-sm transition-all duration-300`,
          railHover ? 'ltr:border-r-2 ltr:border-r-sd-primary rtl:border-l-2 rtl:border-l-sd-primary border-sd-border' : 'border-sd-border',
          collapsed ? 'w-[64px]' : 'w-[210px]',
        )}
      >
        <div className='flex justify-center pt-6 pb-4 items-center overflow-hidden'>
          <Link
            to={ROUTE.ORDERS}
            className='flex items-center justify-center overflow-hidden'
            aria-label='Go to orders'
          >
            <img
              src={boxyIcon}
              alt='Boxy'
              className={cn(
                'shrink-0 transition-all duration-300',
                collapsed ? 'w-8 h-8' : 'w-0 h-8 opacity-0',
              )}
            />
            <img
              src={boxyLogo}
              alt='Boxy'
              className={cn(
                'shrink-0 transition-all duration-300',
                collapsed ? 'w-0 opacity-0' : 'w-[86px] opacity-100',
              )}
            />
          </Link>
        </div>

        <div className='border-b border-sd-border' />

        <div className='px-2 py-4'>
          <NavLink
            to={ROUTE.ADDNEWORDER}
            onClick={(e) => {
              if (!canCreateOrder) e.preventDefault();
            }}
            className={cn(
              'flex items-center rounded-lg py-2.5 text-sm font-medium transition-all duration-300 bg-sd-primary text-sd-primary-foreground overflow-hidden',
              collapsed ? 'justify-center px-2.5 gap-0' : 'justify-center gap-2 px-3',
              canCreateOrder ? 'hover:bg-sd-primary/90' : 'opacity-40 cursor-not-allowed',
            )}
          >
            <span className='text-xl font-medium leading-none shrink-0'>+</span>
            <span
              className={cn(
                'whitespace-nowrap transition-all duration-300',
                collapsed ? 'w-0 opacity-0 overflow-hidden delay-0' : 'w-auto opacity-100 delay-150',
              )}
            >
              {t('new_order')}
            </span>
          </NavLink>
        </div>

        <div className='border-b border-sd-border' />

        <nav className='flex flex-1 flex-col gap-1 px-2 mt-2'>
          {topNavItems.map((item) => {
            const isActive = activeTopNavKey === item.key;
            const iconColor = item.disabled ? '#A8A8B3' : '#38383E';
            return (
              <NavLink
                key={item.key}
                to={item.key}
                onClick={(e) => {
                  if (item.disabled) e.preventDefault();
                }}
                title={item.label}
                className={cn(
                  'flex items-center rounded-lg py-2.5 text-sm font-medium transition-colors',
                  collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                  isActive
                    ? 'bg-sd-primary text-sd-primary-foreground'
                    : 'text-sd-foreground hover:bg-sd-sidebar-accent',
                  item.disabled && 'opacity-40 cursor-not-allowed',
                )}
              >
                {item.renderIcon(iconColor)}
                <span
                  className={cn(
                    'whitespace-nowrap transition-all duration-300',
                    collapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100',
                  )}
                >
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>

        <div className='border-t border-sd-sidebar-border p-3'>
          <button
            onClick={() => {
              if (canOpenTicketCenter) navigate(ROUTE.TICKET_CENTER);
            }}
            className={cn(
              'flex w-full items-center rounded-lg py-2.5 text-sm font-medium transition-colors',
              collapsed ? 'justify-center px-0' : 'gap-3 px-3',
              isTicketCenterActive ? 'bg-sd-primary text-sd-primary-foreground' : 'text-sd-foreground hover:bg-sd-sidebar-accent',
              !canOpenTicketCenter && 'opacity-40 cursor-not-allowed',
            )}
          >
            <CouponIcon color={!canOpenTicketCenter ? '#A8A8B3' : undefined} />
            <span
              className={cn(
                'whitespace-nowrap transition-all duration-300',
                collapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-auto opacity-100',
              )}
            >
              {t('ticket_center')}
            </span>
          </button>

          <div className='border-b border-sd-sidebar-border my-3' />

          <div ref={menuRef} className='relative'>
            <div
              onClick={() => {
                if (menuRef.current) {
                  const rect = menuRef.current.getBoundingClientRect();
                  setMenuCoords({
                    bottom: window.innerHeight - rect.top + 8,
                    left: rect.left,
                    right: window.innerWidth - rect.right,
                    width: rect.width,
                  });
                }
                setMenuOpen((prev) => !prev);
              }}
              className={cn(
                'flex items-center rounded-lg py-2 cursor-pointer hover:bg-sd-sidebar-accent transition-colors',
                collapsed ? 'justify-center px-0' : 'gap-3 px-2',
              )}
            >
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sd-muted text-xs font-semibold'>
                {((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() || '?'}
              </div>
              {!collapsed && (
                <>
                  <div className='flex flex-col whitespace-nowrap overflow-hidden'>
                    <span className='text-[10px] text-sd-muted-foreground capitalize'>
                      {user?.roles?.[0] ?? user?.type?.toLowerCase() ?? ''}
                    </span>
                    <span className='text-sm font-medium text-sd-foreground'>
                      {(user?.first_name ?? '') + ' ' + (user?.last_name ?? '')}
                    </span>
                  </div>
                  <ChevronRight
                    className={cn(
                      'ml-auto h-4 w-4 shrink-0 text-sd-muted-foreground rtl:rotate-180',
                      menuOpen && 'rotate-90 rtl:rotate-90',
                    )}
                  />
                </>
              )}
            </div>

            {menuOpen && createPortal(
              <div
                ref={portalMenuRef}
                className='fixed min-w-[220px] rounded-xl border border-sd-border bg-sd-card shadow-xl px-1 py-1 max-h-[70vh] overflow-y-auto sd-scope sd-font'
                style={{
                  bottom: menuCoords.bottom,
                  left: isRtl ? undefined : menuCoords.left,
                  right: isRtl ? menuCoords.right : undefined,
                  width: Math.max(menuCoords.width, 220),
                  zIndex: 9999,
                }}
              >
                <p className='px-3 py-1.5 text-[11px] font-semibold text-sd-muted-foreground uppercase tracking-wider'>
                  {t('account')}
                </p>
                {accountItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className='flex w-full items-center gap-3 px-3 py-2.5 text-sm text-sd-foreground hover:bg-[#EBDD4C] rounded-[5px] transition-colors no-underline'
                  >
                    {item.node}
                    <span className='flex-1 text-start'>{item.title}</span>
                    <RtlIcon>
                      <ChevronRight className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
                    </RtlIcon>
                  </NavLink>
                ))}

                <div className='border-t border-sd-border my-1' />

                <p className='px-3 py-1.5 text-[11px] font-semibold text-sd-muted-foreground uppercase tracking-wider'>
                  {t('integration')}
                </p>
                {integrationItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className='flex w-full items-center gap-3 px-3 py-2.5 text-sm text-sd-foreground hover:bg-[#EBDD4C] rounded-[5px] transition-colors no-underline'
                  >
                    {item.node}
                    <span className='flex-1 text-start'>{item.title}</span>
                    <RtlIcon>
                      <ChevronRight className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
                    </RtlIcon>
                  </NavLink>
                ))}

                <div className='border-t border-sd-border my-1' />

                <p className='px-3 py-1.5 text-[11px] font-semibold text-sd-muted-foreground uppercase tracking-wider'>
                  {t('action')}
                </p>
                <button
                  onClick={handleLogOut}
                  className='flex w-full items-center gap-3 px-3 py-2.5 text-sm text-sd-destructive hover:bg-[#EBDD4C] rounded-[5px] transition-colors'
                >
                  <LogOut className='h-5 w-5 shrink-0' strokeWidth={1.5} />
                  <span className='flex-1 text-start'>{t('log_out')}</span>
                </button>
              </div>,
              document.body,
            )}
          </div>
        </div>
      </aside>

      <div
        ref={railRef}
        onClick={() => setCollapsed((prev) => !prev)}
        onMouseEnter={() => setRailHover(true)}
        onMouseLeave={() => setRailHover(false)}
        className='absolute ltr:right-0 rtl:left-0 top-0 h-full w-[20px] ltr:translate-x-[10px] rtl:-translate-x-[10px] cursor-pointer z-10 group flex items-center justify-center'
      >
        <div className='h-full w-[3px]' />
      </div>
    </div>
  );
};

export default Sidebar;
